import {removeTrailingSlash} from "./location.helper";
import {ILanguage, IMarket} from "@montana/models/generated/cms.interface";
import {DEFAULT_LANGUAGE, DEFAULT_MARKET} from "@montana/constants/defaults";
import {CUSTOMER_MODE} from "@montana/constants/customer-mode";

export const languageRegex = /^[A-Za-z]{2}$/;
/** @deprecated This regex is deprecated and should not be used in new code */
export const marketRegex = /^[A-Za-z]{2}-[A-Za-z]{2}$/;

export const normalizeLanguageId = (language?: string): string => {
    const normalizedLanguage = language?.trim().toLowerCase();
    return normalizedLanguage && languageRegex.test(normalizedLanguage) ? normalizedLanguage : '';
};

export const getCompleteLanguageList = (
    completeLanguageList?: string[],
    languages?: ILanguage[],
    markets?: IMarket[]
): string[] => {
    const languageList = completeLanguageList?.length
        ? completeLanguageList
        : [
            ...(languages?.map(language => language.languageURLSegment) || []),
            ...(markets?.map(market => market.language) || []),
        ];

    return Array.from(
        new Set(languageList.map(normalizeLanguageId).filter(Boolean))
    );
};

export const isLanguageAllowed = (
    language?: string,
    completeLanguageList?: string[],
    languages?: ILanguage[],
    markets?: IMarket[]
): boolean => {
    const normalizedLanguage = normalizeLanguageId(language);
    return !!normalizedLanguage && getCompleteLanguageList(completeLanguageList, languages, markets).indexOf(normalizedLanguage) >= 0;
};

export const getLanguageSelectorOptions = (
    languages?: ILanguage[],
    activeLanguage?: string,
    completeLanguageList?: string[]
): { value: string; text: string }[] | null => {
    if (!languages) {
        return null;
    }

    const options = languages
        .map(language => ({
            value: normalizeLanguageId(language.languageURLSegment),
            text: language.languageName || normalizeLanguageId(language.languageURLSegment),
        }))
        .filter(option => !!option.value);

    const normalizedActiveLanguage = normalizeLanguageId(activeLanguage);
    const activeLanguageAlreadyVisible = options.some(option => option.value === normalizedActiveLanguage);

    if (
        normalizedActiveLanguage &&
        !activeLanguageAlreadyVisible &&
        isLanguageAllowed(normalizedActiveLanguage, completeLanguageList, languages)
    ) {
        return [
            ...options,
            {
                value: normalizedActiveLanguage,
                text: normalizedActiveLanguage,
            },
        ];
    }

    return options;
};

export const getLanguageSelectorLanguage = (
    languages?: ILanguage[],
    activeLanguage?: string,
    completeLanguageList?: string[]
): ILanguage | undefined => {
    const normalizedActiveLanguage = normalizeLanguageId(activeLanguage);
    const activeVisibleLanguage = languages?.find(language => normalizeLanguageId(language.languageURLSegment) === normalizedActiveLanguage);

    if (activeVisibleLanguage) {
        return activeVisibleLanguage;
    }

    if (isLanguageAllowed(normalizedActiveLanguage, completeLanguageList, languages)) {
        return {
            languageName: normalizedActiveLanguage,
            languageURLSegment: normalizedActiveLanguage,
            isSelected: false,
        };
    }

    return languages?.find(language => language.isSelected);
};

export const transformUrlToLanguageFallback = (url: string, language: string): string => {
    const normalizedLanguage = normalizeLanguageId(language) || DEFAULT_LANGUAGE;
    const context = getLanguageFromUrl(url);
    let normalizedUrl = url || "/";

    if (normalizedUrl.length > 1) {
        normalizedUrl = removeTrailingSlash(normalizedUrl);
    }

    if (/^https?:\/\//.test(normalizedUrl)) {
        return normalizedUrl;
    }

    if (!context) {
        return removeTrailingSlash(`/${normalizedLanguage}${normalizedUrl !== "/" ? normalizedUrl : ""}`);
    }

    return removeTrailingSlash(normalizedUrl.replace(new RegExp(`^/${context}(?=/|\\?|#|$)`), `/${normalizedLanguage}`));
};

export const getLanguageFromUrl = (path: string): string => {
    const segmentedPath = path.split("/");
    let firstSegment =
        segmentedPath.length > 1 ? segmentedPath[1].toLowerCase() : undefined;
    firstSegment = firstSegment?.split("?")[0]; // Ensure no querystring.

    if (
        firstSegment && languageRegex.test(firstSegment)
    ) {
        return normalizeLanguageId(firstSegment);
    }
    return '';
};

export const resolveCurrentUrlLocale = (path?: string, ...fallbackLanguages: (string | undefined)[]): string =>
    getLanguageFromUrl(path || '') ||
    fallbackLanguages.map(normalizeLanguageId).find(Boolean) ||
    DEFAULT_LANGUAGE;

export function getCustomerTypeFromUrl(path: string): string | undefined {
    const segmentedPath = path.split("/");
    if (segmentedPath.indexOf(CUSTOMER_MODE.PROFESSIONAL) >= 0) {
        return CUSTOMER_MODE.PROFESSIONAL;
    }
    return CUSTOMER_MODE.PRIVATE;
}

export function getMarketFromLanguage(markets: IMarket[], language?: string): string {
    const market = markets.find(m => m.language === language)?.marketId || DEFAULT_MARKET;
    return market.toLowerCase();
}

export const transformUrlToLanguage = (
    languages?: ILanguage[],
    url?: string,
    language?: string
): string => {
    const context = !!url ? getLanguageFromUrl(url) : '';

    const defaultLanguage =
        languages?.find(lang => lang.isSelected)?.languageURLSegment || DEFAULT_LANGUAGE;

    // If no url is given, set url to empty string
    if (!url) {
        return "";
    }

    // Remove trailing slash
    if (url.length > 1) {
        url = removeTrailingSlash(url);
    }

    // If the url doens't start with /, then it is an absolute url, which we shouldn't mess with
    if (/^https?:\/\//.test(url)) {
        return url;
    }

    if (!language) {
        return removeTrailingSlash(`/${defaultLanguage}/${url !== "/" ? url : ""}`);
    }

    if (context !== language) {
        // If there is no context, we need to add the language to the url
        return removeTrailingSlash(`/${language}${url}`);
        //return removeTrailingSlash(`/${language}${url !== "/" ? '/' + url.replace(context, "") : ""}`);
    } else {
        // If the url already has a language, we can just return the url with the language
        return removeTrailingSlash(url);
    }
};

export const convertMarketUrlToLanguageUrl = (
    url: string,
    markets: IMarket[] | undefined
): string => {
    const context = getLanguageFromUrl(url);
    if (context && marketRegex.test(context)) {
        const targetMarket = markets?.find(m => m.urlMarket === context);
        if (targetMarket?.language) {
            return url.replace(context, targetMarket.language);
        }
    }
    return url;
};

export const urlWithoutMarket = (path: string): string => {
    return (
        "/" +
        removeTrailingSlash(path)
            .split("/")
            .splice(2)
            .join("/")
    );
};
